import request from '../request';
import i18n, { type SupportedLanguage } from '@/locales';

/** 招聘状态：0-下架，1-上架 */
export type RecruitmentStatus = 0 | 1;

export interface RecruitmentRecord {
  id?: number | string;
  createTime?: string;
  updateTime?: string;
  title?: string;
  targetType: string;
  duty?: string;
  dutySummary?: string;
  requirement?: string;
  status?: RecruitmentStatus;
  langExt?: string | null;
}

export interface RecruitmentPageData {
  records: RecruitmentRecord[];
  total: number;
  size: number;
  current: number;
  pages?: number;
}

export interface RecruitmentPageResult {
  code: number;
  msg: string | null;
  data: RecruitmentPageData;
}

export interface RecruitmentDetailResult {
  code: number;
  msg: string | null;
  data: RecruitmentRecord | null;
}

export interface RecruitmentPageParams {
  current?: number;
  size?: number;
  status?: RecruitmentStatus;
}

type RecruitmentLanguageKey = 'zh' | 'en' | 'jp';

type RecruitmentLocalizedFields = Partial<
  Pick<RecruitmentRecord, 'title' | 'targetType' | 'duty' | 'dutySummary' | 'requirement'>
>;

const recruitmentLanguageMap: Record<SupportedLanguage, RecruitmentLanguageKey> = {
  'zh-CN': 'zh',
  'en-US': 'en',
  'ja-JP': 'jp',
};

function hasValue(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function decodeRecruitmentField(value: string): string {
  if (!value.startsWith('b64:')) return value;

  try {
    const binary = atob(value.slice(4));
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return value;
  }
}

function parseLocalizedFields(langExt?: string | null): RecruitmentLocalizedFields | undefined {
  if (!hasValue(langExt)) return undefined;

  try {
    const parsed = JSON.parse(langExt) as Record<string, RecruitmentLocalizedFields>;
    const languageKey = recruitmentLanguageMap[
      (i18n.resolvedLanguage || i18n.language || 'zh-CN') as SupportedLanguage
    ] ?? 'zh';
    return parsed[languageKey];
  } catch {
    return undefined;
  }
}

function localizeRecruitmentRecord(record: RecruitmentRecord): RecruitmentRecord {
  const localizedFields = parseLocalizedFields(record.langExt);
  if (!localizedFields) return record;

  return {
    ...record,
    ...(hasValue(localizedFields.title) ? { title: decodeRecruitmentField(localizedFields.title) } : {}),
    ...(hasValue(localizedFields.targetType)
      ? { targetType: decodeRecruitmentField(localizedFields.targetType) }
      : {}),
    ...(hasValue(localizedFields.duty) ? { duty: decodeRecruitmentField(localizedFields.duty) } : {}),
    ...(hasValue(localizedFields.dutySummary)
      ? { dutySummary: decodeRecruitmentField(localizedFields.dutySummary) }
      : {}),
    ...(hasValue(localizedFields.requirement)
      ? { requirement: decodeRecruitmentField(localizedFields.requirement) }
      : {}),
  };
}

/** POST /jdx/mini/recruitment/page */
export function recruitmentPage(
  params?: RecruitmentPageParams,
): Promise<RecruitmentPageResult> {
  return request
    .post<RecruitmentPageResult, RecruitmentPageParams>({
      url: '/jdx/mini/recruitment/page',
      data: {
        current: 1,
        size: 10,
        status: 1,
        ...params,
      },
    })
    .then(result => ({
      ...result,
      data: {
        ...result.data,
        records: result.data.records.map(localizeRecruitmentRecord),
      },
    }));
}

/** POST /jdx/mini/recruitment/detail */
export function recruitmentDetail(
  recruitmentId: number | string,
): Promise<RecruitmentDetailResult> {
  return request
    .post<RecruitmentDetailResult, { recruitmentId: number }>({
      url: '/jdx/mini/recruitment/detail',
      data: { recruitmentId: Number(recruitmentId) },
    })
    .then(result => ({
      ...result,
      data: result.data ? localizeRecruitmentRecord(result.data) : null,
    }));
}
